import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import { DefaultRouteAction, DefaultRouteParam } from "constants/routes";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FormMode, formUtils } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import styles from "./policy-panel.module.scss";
import { Formatters } from "helper/formatters";
import {
  IAMEntityNameSection,
  IIAMEntityNameSectionErrors,
} from "../../shared-ui/iam/sections/entity-name/entity-name-section";
import { iamService } from "api/services/iam";
import DetailsPanelFooter from "components/shared-ui/details-panel-footer/details-panel-footer";
import { IAMEntity } from "utils/validators";
import {
  IAMPolicyType,
  IIAMPolicy,
  IIAMPolicyCreateRequest,
  IIAMPolicyVersion,
} from "api/entities/iam-policies";
import { IIAMPolicyDescriptionSectionErrors } from "./sections/policy-description-section";
import {
  IAMPolicyDocumentSection,
  IIAMPolicyDocumentSectionErrors,
} from "./sections/policy-document-section";
import { json } from "stream/consumers";
import { Label } from "components/shared-ui/label";

export interface IAMPolicyPanelProps {
  mode?: FormMode;
  embedded: boolean;
  onClose: () => void;
  fetchPage?: () => void;
}

const DefaultPolicy = {
  Version: "2012-10-17",
  Statement: [],
};

const defaultCreateRequest: IIAMPolicyCreateRequest = {
  PolicyDocument: JSON.stringify(DefaultPolicy, null, 2),
  PolicyName: "",
  Description: "",
};

interface IAMPolicyPanelErrors
  extends
    IIAMEntityNameSectionErrors,
    IIAMPolicyDescriptionSectionErrors,
    IIAMPolicyDocumentSectionErrors {}

export default function IAMPolicyPanel(props: IAMPolicyPanelProps) {
  const { idOrAction } = useParams<{
    [DefaultRouteParam.IdOrAction]: string;
  }>();

  const [policyApi, isPolicyLoading] = useApi();
  const [policyVersionApi, isPolicyVersionLoading] = useApi();

  const isPanelOpened = props.embedded
    ? props.mode === FormMode.Create
    : !!idOrAction;
  const isPolicyCreation = props.embedded
    ? props.mode === FormMode.Create
    : idOrAction === DefaultRouteAction.New;

  const policyArn = isPolicyCreation ? null : decodeURIComponent(idOrAction);

  const [policy, setPolicy] = useState<IIAMPolicy>(null);
  const [policyVersion, setPolicyVersion] = useState<IIAMPolicyVersion>(null);

  const [errors, setErrors] = useState<IAMPolicyPanelErrors | null>(null);
  const [policyCreateRequest, setPolicyCreateRequest] =
    useState<IIAMPolicyCreateRequest>(defaultCreateRequest);

  const canProceed = () => {
    const modified = isPolicyCreation ? true : isPolicyModified();
    return (
      !!errors &&
      !Object.keys(errors).some((key) => errors[key]) &&
      !!policyCreateRequest.PolicyName &&
      modified
    );
  };

  const isPolicyModified = () => {
    try {
      const current = JSON.parse(policyVersion.Document);
      const updated = JSON.parse(policyCreateRequest.PolicyDocument);
      return JSON.stringify(current) !== JSON.stringify(updated);
    } catch {
      return false;
    }
  };

  const closeAndRefresh = () => {
    props.fetchPage?.();
    handleClose();
  };
  const handleClose = () => {
    props.onClose();
  };

  const handleError = (errors: Partial<IAMPolicyPanelErrors>) => {
    setErrors((prevErrors) => ({ ...prevErrors, ...errors }));
  };

  const handleChange = (policyUpdate: Partial<IIAMPolicyCreateRequest>) => {
    setPolicyCreateRequest((prev) => ({ ...prev, ...policyUpdate }));
  };

  const save = async () => {
    const request = formUtils.trimStringValues(policyCreateRequest);
    if (isPolicyCreation) {
      return policyApi(iamService.createPolicy(request)).then((policy) => {
        closeAndRefresh();
      });
    } else {
      return policyApi(
        iamService
          .updatePolicy({
            PolicyArn: policyArn,
            PolicyDocument: request.PolicyDocument,
            Description: request.Description,
          })
          .then((policy) => {
            closeAndRefresh();
          }),
      );
    }
  };

  useEffect(() => {
    if (isPanelOpened) {
      if (isPolicyCreation) {
        setPolicyCreateRequest(defaultCreateRequest);
        setPolicyVersion({
          Document: defaultCreateRequest.PolicyDocument,
          Type: IAMPolicyType.Custom,
        });
        setPolicy(null);
      } else {
        policyVersionApi(
          iamService.getPolicyVersion({ PolicyArn: policyArn }),
        ).then((version) => {
          setPolicyVersion(version);
          handleChange({ PolicyDocument: version.Document });
        });
        policyApi(iamService.getPolicy(policyArn)).then((policy) => {
          handleChange({ ...policy });
          setPolicy(policy);
        });
      }

      setErrors(null);
    }
    // eslint-disable-next-line
  }, [isPanelOpened]);

  return (
    <>
      <DetailsPanel
        open={isPanelOpened}
        isLoading={isPolicyLoading || isPolicyVersionLoading}
        onCloseClick={props.onClose}
        title={isPolicyCreation ? "Add New Policy" : "Policy Details"}
        className={styles.panel}
      >
        {isPanelOpened && (
          <div className={styles["policy-info"]}>
            {!!policy && policyVersion && (
              <>
                <section className={styles["policy-info-block"]}>
                  <section className={styles["created-uri"]}>
                    {!isPolicyCreation && (
                      <Label label="Name" value={policy.PolicyName} />
                    )}
                    <Label label="ID" value={policy.PolicyId} />
                    <Label label="Resource ARN" value={policy.Arn} />
                    <Label label="Type" value={policyVersion.Type} />
                    <Label
                      label="Attachment Count"
                      value={policy.AttachmentCount}
                    />
                    <Label
                      label="Created At"
                      value={Formatters.formatDateTime(policy.CreateDate)}
                    />
                  </section>
                </section>
              </>
            )}

            {isPolicyCreation && (
              <div className={styles.description}>
                A Policy is a structured JSON document in Myota that defines a
                set of permissions governing what actions are allowed or denied
                on specific resources. It does not grant access by itself but
                serves as a rule set that can be attached to users, groups, or
                roles to control their behavior. Each policy consists of
                statements specifying the effect (Allow or Deny), the actions
                that can be performed, and the resources those actions apply to.
                By assigning policies, administrators can precisely manage
                access to Myota services, enforce security best practices, and
                ensure compliance through centralized and consistent permission
                management.
              </div>
            )}

            {isPolicyCreation && (
              <IAMEntityNameSection
                className={styles.name}
                request={policyCreateRequest}
                field="PolicyName"
                type={IAMEntity.Policy}
                onChange={handleChange}
                onError={handleError}
                errors={errors}
              />
            )}

            {/* <IAMPolicyDescriptionSection
              disabled={!isPolicyCreation}
              errors={errors}
              onChange={handleChange}
              onError={handleError}
              request={policyCreateRequest}
              className={styles.name}
            /> */}
            <IAMPolicyDocumentSection
              value={policyCreateRequest.PolicyDocument}
              isPolicyCreation={isPolicyCreation}
              errors={errors}
              onChange={handleChange}
              onError={handleError}
              readonly={policyVersion?.Type === IAMPolicyType.Managed}
            />

            <DetailsPanelFooter
              sticky={true}
              saveButtonText={"Save"}
              saveButtonDisabled={!canProceed()}
              onSaveButtonClick={save}
              onCancelButtonClick={handleClose}
            />
          </div>
        )}
      </DetailsPanel>
    </>
  );
}
